import { pending, fulfilled, rejected, loadStatus } from 'lib/promiseMiddlewareHelpers'
import fetchJSON from 'lib/fetchJSON'
import { keyIn } from 'lib/utils'
import { fm } from 'lib/localization'
import Immutable, { Map } from 'immutable'
import moment from 'moment'
import { defineMessages } from 'react-intl'

export const LOAD_GOAL = 'goals/LOAD_GOAL'
export const CREATE_GOAL = 'goals/CREATE_GOAL'
export const UPDATE_GOAL = 'goals/UPDATE_GOAL'
export const SAVE_GOAL = 'goals/SAVE_GOAL'
export const DELETE_GOAL = 'goals/DELETE_GOAL'

const deserializeGoal = (fromRails) => Immutable.fromJS(fromRails.goal).merge({ loadStatus: loadStatus.fulfilled })
// serialize goal for updates (not create)

export const reducer = (state = Map(), action = {}) => {
  switch (action.type) {
    case pending(LOAD_GOAL): {
      const { meta: { goalId } } = action
      const goal = state.get(goalId, Map()).merge({ loadStatus: loadStatus.pending })
      return state.set(goalId, goal)
    }
    case fulfilled(LOAD_GOAL): {
      const { meta: { goalId }, payload } = action
      const goal = state.get(goalId, Map()).merge(deserializeGoal(payload))
      return state.set(goalId, goal)
    }
    case rejected(LOAD_GOAL): {
      const { meta: { goalId } } = action
      const goal = state.get(goalId, Map()).merge({ loadStatus: loadStatus.rejected })
      return state.set(goalId, goal)
    }
    case fulfilled(CREATE_GOAL): {
      const { payload } = action
      // todo set goal participant as part of this action, too
      return state.set(payload.goal.id, deserializeGoal(payload))
    }
    case fulfilled(DELETE_GOAL): {
      const { goalId } = action.meta
      return state.delete(goalId)
    }
    case fulfilled(SAVE_GOAL): {
      const goalId = action.meta.goal.get('id')
      const goal = state.get(goalId, Map()).merge(deserializeGoal(action.payload))
      return state.set(goalId, goal)
    }
    case UPDATE_GOAL: {
      const { update, goalId } = action
      return state.set(goalId, state.get(goalId).merge(update))
    }
    default:
      return state
  }
}

export const loadGoal = (goalId) => ({
  type: LOAD_GOAL,
  meta: { goalId },
  payload: fetchJSON(`/goals/${goalId}.json`)
})

// goal here is an immutable object where the
// starts_on and ends_on values are Moment objects or null
export const createGoal = (goal) => {
  const body = new window.FormData()

  const withStringDates = goal.merge({
    starts_on: goal.get('starts_on').toISOString(), // required, so no null check
    ends_on: goal.get('ends_on') && goal.get('ends_on').toISOString()
  }).delete('icon') // strip icon until backend gets on board with modifiable images

  body.append('goal', JSON.stringify(withStringDates.toJS()))

  if (goal.get('icon')) {
    body.append('icon', goal.get('icon'))
  }
  const payload = fetchJSON('/goals', null, {
    method: 'POST',
    body,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  })

  return {
    type: CREATE_GOAL,
    meta: { goal: withStringDates },
    payload
  }
}

export const updateGoal = (goalId, update) => ({
  type: UPDATE_GOAL,
  goalId,
  update
})

const serializeGoalUpdate = (goal) => {
  const goalJSON = goal.filter(keyIn(['name', 'description'])).toJS()

  if (goal.get('icon') instanceof window.File) {
    const body = new window.FormData()
    body.append('goal', JSON.stringify(goalJSON))
    body.append('icon', goal.get('icon'))
    return body
  }

  return { goal: goalJSON }
}

export const saveGoal = (goal) => {
  const body = serializeGoalUpdate(goal)
  let payload
  if (body instanceof window.FormData) {
    payload = fetchJSON(`/goals/${goal.get('id')}.json`, null, {
      method: 'PATCH',
      body,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
  } else {
    payload = fetchJSON(
      `/goals/${goal.get('id')}.json`,
      body,
      { method: 'PATCH' }
    )
  }
  return {
    type: SAVE_GOAL,
    meta: { goal },
    payload
  }
}

export const deleteGoal = (goalId) => ({
  type: DELETE_GOAL,
  meta: { goalId },
  payload: fetchJSON(`/goals/${goalId}.json`, null, { method: 'DELETE' })
})

const errorMessages = defineMessages({
  target: {
    id: 'goalErrors.target',
    defaultMessage: 'Oops! You need to specify a target for your goal'
  },
  name: {
    id: 'goalErrors.name',
    defaultMessage: "Name can't be blank"
  }
})
export const validateGoal = (goal, formType, newGoal, intl) => {
  const errors = {}
  if (newGoal && formType !== 'leaderboard' && !goal.getIn(['goal_params', 'max'])) {
    errors[formType] = [fm(intl, errorMessages.target)]
  }
  if (!goal.get('name') || goal.get('name').length === 0) {
    errors['name'] = [fm(intl, errorMessages.name)]
  }
  // no date validations because that should be enforced by the date picker
  if (Object.keys(errors).length > 0) {
    return { goalValidates: false, errors }
  } else {
    return { goalValidates: true, errors }
  }
}

export const getDefaultGoal = (type) => Immutable.fromJS({
  name: '',
  icon: null,
  description: '',
  starts_on: new Date(),
  ends_on: moment().add(1, 'month').toDate(),
  visibility: 0,
  goal_type: type === 'leaderboard' ? 'distance' : type,
  goal_params: {}
})
